import { oc } from "@orpc/contract";
import { z } from "zod";

export const SidebarNavSectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  itemIds: z.array(z.string()),
});

export const SidebarNavLayoutSchema = z.object({
  sections: z.array(SidebarNavSectionSchema),
  // Items hidden from the sidebar but kept in their section slot (so toggling
  // visibility preserves their position). A subset of the section itemIds.
  hiddenItemIds: z.array(z.string()).default([]),
  // Items removed from all sections into the "Unused" bucket. Not rendered in
  // the sidebar and not re-added automatically.
  unusedItemIds: z.array(z.string()).default([]),
  // Sections hidden from the sidebar (all their items excluded) but kept in
  // place in the editor. A subset of section ids.
  hiddenSectionIds: z.array(z.string()).default([]),
});

// A user-defined Quick Find (⌘K) alias: typing `shortcut` surfaces the page
// at `path` first. Paths are route full paths, not ids, so settings sections
// (which aren't routes with nav metadata) can be targets too.
export const MAX_QUICK_FIND_SHORTCUTS = 100;

export const QuickFindShortcutSchema = z.object({
  shortcut: z.string().trim().toLowerCase().min(1).max(32),
  path: z.string().min(1),
});

export const SidebarPreferencesSchema = z.object({
  collapsedGroups: z.array(z.string()).default([]),
  hiddenItems: z.array(z.string()).default([]),
  layout: SidebarNavLayoutSchema.optional(),
  // Icon-collapsed (desktop). Optional so older rows stay "never saved" and
  // first paint can still fall back to the sidebar cookie.
  collapsed: z.boolean().optional(),
  // Absent = never customized, so the built-in shortcut templates apply.
  // Present (even empty) replaces them entirely.
  quickFindShortcuts: z
    .array(QuickFindShortcutSchema)
    .max(MAX_QUICK_FIND_SHORTCUTS)
    .optional(),
});

// Remembers the buyer's last-used division selection on the procurement
// replenishment page so it survives across sessions (the URL stays the
// shareable source of truth; this only seeds the default when none is set).
export const ProcurementDivisionFilterSchema = z.object({
  divisionIds: z.array(z.string()).default([]),
});

// Remembers the buyer's last-used destination-warehouse selection on the
// replenishment page (scoped to the selected divisions). Same role as the
// division filter above — seeds the default when the URL carries none.
export const ProcurementWarehouseFilterSchema = z.object({
  warehouseIds: z.array(z.string()).default([]),
});

// Remembers the buyer's last-used buy-vs-transfer view on the replenishment
// page: 'all' (supplier carts + transfers), 'po' (purchases only), 'to'
// (transfers only).
export const ProcurementViewModeSchema = z.object({
  view: z.enum(["all", "po", "to"]).default("all"),
});

// Per-user delivery choice for the notification system. In-app is always on
// (the bell feed is the baseline); "both" opts in to email on top. Defaults
// to in-app only. NotificationService intersects an event's channels with
// this preference per recipient; when the intersection is empty it falls
// back to in-app so a notification is never silently dropped.
export const NotificationDeliveryChannelSchema = z.enum(["in_app", "both"]);

export const NotificationDeliveryPreferenceSchema = z.object({
  channel: NotificationDeliveryChannelSchema.default("in_app"),
});

export const UserLocaleSchema = z.enum(["en", "en-CA", "fr-CA", "es", "pt-BR"]);

export const LanguagePreferenceSchema = z.object({
  locale: UserLocaleSchema.default("en"),
});

// Per-user email signature appended to outgoing emails (AR statements first;
// reusable by other flows). The HTML is rich text that may embed signature
// images as `<img data-document-id="...">`. The document id is the source of
// truth for an image; the `src` attribute is regenerated per context (a fresh
// signed URL for in-app preview, a `cid:` reference at send time). `enabled`
// lets a user keep a saved signature but stop appending it without deleting it.
export const EmailSignatureSchema = z.object({
  html: z.string().default(""),
  enabled: z.boolean().default(true),
});

// How often and how recently the user opened each page, keyed by the matched
// route's full path (`/orders`, `/orders/$orderId`). Feeds the personalized
// Home page; bounded so the stored blob stays small.
export const PAGE_USAGE_MAX_TRACKED = 60;

export const PageUsagePreferencesSchema = z.object({
  pages: z
    .record(
      z.string().max(200),
      z.object({
        count: z.number().int().min(1),
        lastVisitedAt: z.iso.datetime(),
      }),
    )
    .refine((pages) => Object.keys(pages).length <= PAGE_USAGE_MAX_TRACKED, {
      message: `At most ${PAGE_USAGE_MAX_TRACKED} pages are tracked`,
    }),
});
export type PageUsagePreferences = z.infer<typeof PageUsagePreferencesSchema>;

// Sparse overrides for global keyboard shortcuts. Canonical ids and defaults
// live in the frontend registry (`frontend/src/lib/hotkeys/registry.ts`).
// Missing ids fall back to the app default. Values are react-hotkeys-hook
// combos such as "mod+k".
export const KeyboardShortcutsPreferencesSchema = z.object({
  overrides: z.record(z.string(), z.string().min(1)).default({}),
});

const preferenceValueSchemas = {
  sidebar: SidebarPreferencesSchema,
  procurementDivisionFilter: ProcurementDivisionFilterSchema,
  procurementWarehouseFilter: ProcurementWarehouseFilterSchema,
  procurementViewMode: ProcurementViewModeSchema,
  notificationDelivery: NotificationDeliveryPreferenceSchema,
  language: LanguagePreferenceSchema,
  emailSignature: EmailSignatureSchema,
  pageUsage: PageUsagePreferencesSchema,
  keyboardShortcuts: KeyboardShortcutsPreferencesSchema,
} as const;

export type PreferenceKey = keyof typeof preferenceValueSchemas;

export const PreferenceKeySchema = z.enum(
  Object.keys(preferenceValueSchemas) as [PreferenceKey, ...PreferenceKey[]],
);

export const PreferenceValueSchema = z.record(z.string(), z.unknown());

export function getValueSchemaForKey(key: PreferenceKey) {
  return preferenceValueSchemas[key];
}

export const GetUserPreferenceInputSchema = z.object({
  key: PreferenceKeySchema,
});

export const GetUserPreferenceResponseSchema = z
  .object({
    key: z.string(),
    value: PreferenceValueSchema,
  })
  .nullable();

export const UpsertUserPreferenceInputSchema = z.object({
  key: PreferenceKeySchema,
  value: PreferenceValueSchema,
});

export const UpsertUserPreferenceResponseSchema = z.object({
  key: z.string(),
  value: PreferenceValueSchema,
});

export const userPreferencesContracts = {
  get: oc
    .input(GetUserPreferenceInputSchema)
    .output(GetUserPreferenceResponseSchema)
    .route({
      method: "GET",
      summary: "Get one of my preferences",
      description:
        "Returns the signed-in user's saved value for one preference, such as sidebar, procurement filters, notification delivery, language, email signature, or keyboard shortcuts.",
    }),
  upsert: oc
    .input(UpsertUserPreferenceInputSchema)
    .output(UpsertUserPreferenceResponseSchema)
    .route({
      method: "POST",
      summary: "Save one of my preferences",
      description:
        "Saves the signed-in user's value for one preference, such as sidebar, procurement filters, notification delivery, language, or keyboard shortcuts, replacing the previous value.",
    }),
} as const;

export type GetUserPreferenceInput = z.infer<
  typeof GetUserPreferenceInputSchema
>;
export type GetUserPreferenceResponse = z.infer<
  typeof GetUserPreferenceResponseSchema
>;
export type UpsertUserPreferenceInput = z.infer<
  typeof UpsertUserPreferenceInputSchema
>;
export type UpsertUserPreferenceResponse = z.infer<
  typeof UpsertUserPreferenceResponseSchema
>;
export type EmailSignature = z.infer<typeof EmailSignatureSchema>;
export type SidebarPreferences = z.infer<typeof SidebarPreferencesSchema>;
export type NotificationDeliveryChannel = z.infer<
  typeof NotificationDeliveryChannelSchema
>;
export type NotificationDeliveryPreference = z.infer<
  typeof NotificationDeliveryPreferenceSchema
>;
export type LanguagePreference = z.infer<typeof LanguagePreferenceSchema>;
export type UserLocale = z.infer<typeof UserLocaleSchema>;
export type ProcurementDivisionFilter = z.infer<
  typeof ProcurementDivisionFilterSchema
>;
export type SidebarNavLayout = z.infer<typeof SidebarNavLayoutSchema>;
export type SidebarNavSection = z.infer<typeof SidebarNavSectionSchema>;
export type QuickFindShortcut = z.infer<typeof QuickFindShortcutSchema>;
export type KeyboardShortcutsPreferences = z.infer<
  typeof KeyboardShortcutsPreferencesSchema
>;
